import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { X, ShoppingCart, Check } from 'lucide-react'
import { Product } from '../../types'
import Button from '../ui/Button'

interface CartPopupProps {
  isOpen: boolean
  onClose: () => void
  product: Product | null
  quantity: number
}

export default function CartPopup({ isOpen, onClose, product, quantity }: CartPopupProps) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      // Auto close after 5 seconds
      const timer = setTimeout(() => {
        handleClose()
      }, 5000)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isOpen])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 300)
  }

  if (!isOpen || !product) return null

  return (
    <div className={`fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 transition-all duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0'
    }`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Popup */}
      <div className={`relative bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-purple-200/50 max-w-md w-full transform transition-all duration-300 ${
        isVisible ? 'translate-y-0 scale-100' : '-translate-y-4 scale-95'
      }`}>
        {/* Success Header */}
        <div className="flex items-center justify-between p-4 border-b border-purple-200/30">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
              <Check className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Sepete Eklendi!</h3>
              <p className="text-sm text-gray-600">Ürün başarıyla sepete eklendi</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Product Info */}
        <div className="p-4">
          <div className="flex items-center gap-4">
            {product.image && (
              <img
                src={product.image}
                alt={product.name}
                className="w-16 h-16 object-cover rounded-xl border-2 border-purple-200/50"
              />
            )}
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 line-clamp-2">{product.name}</h4>
              <p className="text-sm text-gray-600 mt-1">Adet: {quantity}</p>
              <p className="text-lg font-bold text-purple-600 mt-1">
                ₺{(product.price * quantity).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 pt-0">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1"
          >
            Alışverişe Devam Et
          </Button>
          <Link to="/sepet" className="flex-1" onClick={handleClose}>
            <Button 
              variant="primary"
              className="w-full flex items-center justify-center gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Sepete Git
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}